import { Alert, Box, Link } from '@mui/material';
import {
  FormEvent,
  KeyboardEventHandler,
  useCallback,
  useRef,
  useState,
} from 'react';

import OneTimePassword from './OneTimePassword';

import TextInput from '@/components/elements/input/TextInput';
import LoadingButton from '@/components/elements/LoadingButton';
import {
  fetchCurrentUser,
  isHmisResponseError,
  login,
} from '@/modules/auth/api/sessions';
import {
  dispatchAccountErrorEvent,
  isTerminalAccountErrorType,
} from '@/modules/auth/events';
import { useHmisAppSettings } from '@/modules/hmisAppSettings/useHmisAppSettings';
import { reloadWindow } from '@/utils/location';

const errorMessage = (error: Error) => {
  if (isHmisResponseError(error)) {
    return error.type === 'unauthenticated'
      ? 'Incorrect email or password.'
      : error.message;
  }
  return 'Something went wrong, please try again later.';
};

const LoginForm = () => {
  const [error, setError] = useState<Error>();
  const [requiredFieldsError, setRequiredFieldsError] =
    useState<boolean>(false);
  const [prompt2fa, setPrompt2fa] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { resetPasswordUrl } = useHmisAppSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Reload page on successful login, to ensure the user fetches the latest assets.
  // When the page reloads, the application will get the user from getValidCachedUser
  const handleSuccess = useCallback(() => reloadWindow(), []);

  const retryCsrf = useRef(true);
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement | HTMLDivElement>) => {
      event.preventDefault();
      if (!email || !password) {
        setRequiredFieldsError(true);
        return;
      }
      setRequiredFieldsError(false);
      setError(undefined);
      setLoading(true);

      const sendLogin = () => {
        // if (simulateFail) return Promise.reject( new HmisResponseError({ type: 'unverified_request' }));
        return login({ email, password }).then(() => {
          setPrompt2fa(false);
          setLoading(false);
          handleSuccess();
        });
      };

      const handleHmisError = (error: any): boolean => {
        if (!isHmisResponseError(error)) return false;

        // Not a form error: the provider replaces this form with the terminal page.
        if (isTerminalAccountErrorType(error.type)) {
          setLoading(false);
          dispatchAccountErrorEvent(error.type);
          return true;
        }

        if (error.type === 'mfa_required') {
          setError(undefined);
          setPrompt2fa(true);
          setLoading(false);
          return true;
        } else if (error.type === 'unverified_request' && retryCsrf.current) {
          // fetch the current user to get a new csrf cookie
          retryCsrf.current = false;
          fetchCurrentUser()
            .then(({ user: currentUser }) => {
              if (currentUser) return reloadWindow(); // already logged in

              // retry the login
              sendLogin().catch((error: any) => {
                if (!handleHmisError(error)) {
                  setError(error);
                  setLoading(false);
                }
              });
            })
            .catch((error) => {
              setError(error);
              setLoading(false);
            });
          return true;
        }
        return false;
      };

      return sendLogin().catch((error: any) => {
        if (!handleHmisError(error)) {
          setError(error);
          setLoading(false);
        }
      });
    },
    [handleSuccess, email, password]
  );

  // For the password field, restore html's default implicit form submission behavior, which we override in TextInput
  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  if (prompt2fa) return <OneTimePassword onSuccess={handleSuccess} />;

  return (
    <Box component='form' onSubmit={handleSubmit} noValidate>
      <TextInput
        margin='normal'
        required
        fullWidth
        label='Email Address'
        name='email'
        autoComplete='email'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder='ex. name@email.com'
        sx={{ '.MuiFormLabel-asterisk': { display: 'none' } }}
        {...(requiredFieldsError && !email
          ? { error: true, helperText: 'Please enter email' }
          : undefined)}
      />
      <TextInput
        margin='normal'
        required
        fullWidth
        name='password'
        label='Password'
        type='password'
        placeholder='Password'
        autoComplete='current-password'
        value={password}
        onKeyDown={onKeyDown}
        onChange={(e) => setPassword(e.target.value)}
        sx={{ '.MuiFormLabel-asterisk': { display: 'none' } }}
        {...(requiredFieldsError && !password
          ? { error: true, helperText: 'Please enter password' }
          : undefined)}
      />

      <LoadingButton
        type='submit'
        variant='contained'
        color='primary'
        fullWidth
        sx={{ mt: 3, mb: 2 }}
        loading={loading}
      >
        Sign In
      </LoadingButton>
      {error && <Alert severity='error'>{errorMessage(error)}</Alert>}
      {resetPasswordUrl && (
        <Box
          sx={{ width: '100%', display: 'flex', mt: 2, justifyContent: 'end' }}
        >
          <Link href={resetPasswordUrl}>Forgot password?</Link>
        </Box>
      )}
    </Box>
  );
};

export default LoginForm;
